import React, {
  ComponentPropsWithoutRef,
  forwardRef,
  useRef,
  useState
} from 'react';
import { clsx } from 'clsx';

import CheckBoxOff from '@strava/icons/ActionsCheckBoxOffSmall';
import CheckBoxOn from '@strava/icons/ActionsCheckBoxOnSmall';
import { useMergeRefs } from '@strava/react-hooks/use-merge-refs';
import { color as colors } from '@strava/ui-tokens/js';

import styles from './Checkbox.module.scss';

export interface CheckboxProps
  extends Omit<ComponentPropsWithoutRef<'input'>, 'onChange'> {
  name: string;
  color?: string;
  colorOff?: string;
  checked?: boolean;
  disabled?: boolean;
  className?: string;
  onChange?: (checked: boolean) => void;
}

export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(
  (
    {
      name,
      color = colors.colorCoreO3,
      colorOff = colors.colorExtendedNeutralN2,
      checked: controlledChecked,
      disabled = false,
      readOnly = false,
      className,
      onChange: controlledOnChange,
      defaultChecked = false,
      ...props
    },
    ref
  ) => {
    const [checkedState, setCheckedState] = useState(defaultChecked);
    const inputRef = useRef<HTMLInputElement>(null);

    const mergedRefs = useMergeRefs([ref, inputRef]);

    const isControlled = controlledChecked !== undefined;
    const checked = isControlled ? controlledChecked : checkedState;

    const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      if (!isControlled) {
        setCheckedState(e.target.checked);
      }

      controlledOnChange?.(e.target.checked);
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter' || e.key === ' ' || e.key === 'Space') {
        e.preventDefault();
        if (!isControlled) {
          setCheckedState((p) => !p);
        }

        const currentCheckedValue = inputRef?.current?.checked ?? false;
        controlledOnChange?.(!currentCheckedValue);
      }
    };

    return (
      <span
        className={clsx(styles.check, className, {
          [styles.noInput as string]: disabled || readOnly
        })}
        aria-checked={checked}
        aria-disabled={disabled}
        onKeyDown={handleKeyDown}
        tabIndex={disabled ? -1 : 0}
      >
        <input
          type="checkbox"
          id={name}
          name={name}
          checked={checked}
          disabled={disabled}
          readOnly={readOnly}
          onChange={!readOnly ? onChange : undefined}
          ref={mergedRefs}
          onKeyDown={handleKeyDown}
          tabIndex={-1}
          {...props}
        />

        {checked ? (
          <CheckBoxOn fill={color} />
        ) : (
          <CheckBoxOff fill={colorOff} />
        )}
      </span>
    );
  }
);
